<template>
  <div
    class="v3-custom-time-picker custom-vue-time-picker ps__child--consume w-100"
  >
    <VueDatePicker
      class="v3-input-field"
      :class="[classes, { 'is-invalid': invalidFeedback }]"
      ref="datepicker"
      :teleport="false"
      :id="resolvedInputId"
      :aria-labels="ariaLabelsForPicker"
      :model-type="format ? format : 'hh:mm'"
      autocomplete="off"
      auto-apply
      :format="pickerTimeFormat ? pickerTimeFormat : 'hh:mm aa'"
      :is-24="!!showSeconds"
      time-picker
      arrow-navigation
      :config="datePickerConfig"
      :clearable="false"
      :hide-input-icon="hideIcon"
      @update:model-value="onValueChange"
      :model-value="modelValue"
      :placeholder="placeholder"
      :enable-seconds="!!showSeconds"
      :disabled="!!disabled"
      :readonly="!!readonly"
      :text-input="timePickerTextInputOptions"
      @open="onPickerOpen"
      @closed="onPickerClosed"
      @blur="onBlur"
      :on-click-outside="onClickOutside"
    >
      <template #input-icon>
        <i class="ri-time-line" aria-hidden="true"></i>
      </template>
      <template #arrow-up>
        <i class="fi flaticon-up-arrow"></i>
      </template>
      <template #arrow-down>
        <i class="fi flaticon-down-arrow"></i>
      </template>
      <template #clock-icon> </template>
    </VueDatePicker>
    <div
      v-if="invalidFeedbackMessage"
      :id="invalidFeedbackId"
      class="invalid-message text-start"
      role="alert"
      aria-live="assertive"
    >
      {{ invalidFeedbackMessage }}
    </div>
    <slot name="footer"> </slot>
  </div>
</template>
<script>
import {
  ref,
  watch,
  computed,
  getCurrentInstance,
  watchEffect,
  nextTick,
  onUnmounted
} from "vue"
import moment from "moment-timezone"
import "@vuepic/vue-datepicker/dist/main.css"

let customTimePickerFallbackId = 0
const INPUT_ICON_SELECTOR = ".dp__input_icon"
const MENU_WRAP_SELECTOR = ".dp__outer_menu_wrap"
const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  '[href]:not([href=""])',
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(", ")
const PRE_DISABLED_TABINDEX_ATTR = "data-pre-disabled-tabindex"

/** `data-test` on @vuepic time column middle button, e.g. `hours-toggle-overlay-btn-0`. */
const segmentFromTimeDisplayBtn = (btn) => {
  const test = btn?.getAttribute?.("data-test") || ""
  if (test.startsWith("hours-")) return "hours"
  if (test.startsWith("minutes-")) return "minutes"
  if (test.startsWith("seconds-")) return "seconds"
  return null
}

export default {
  name: "CustomTimePicker",
  props: [
    "value",
    "placeholder",
    "classes",
    "readonly",
    "disabled",
    "inputId",
    "format",
    "hideIcon",
    "innerLabel",
    "disableSetFocus",
    "modelValue",
    "show",
    "showSeconds",
    "pickerTimeFormat",
    "invalidFeedback",
    /** Visible field label for WCAG 2.5.3 / 4.1.2 (matches `<Label>` text). */
    "accessibleLabel",
    /** Id of external `<label>` (`Label` `label-id`) for `aria-labelledby` when present. */
    "labelId"
  ],
  emits: ["update:modelValue", "input", "change", "focus", "blur"],
  setup(props, { emit }) {
    // Vue 3.5+ has useId(); project is on Vue 3.4 — use instance uid for stable unique ids.
    const instanceUid =
      getCurrentInstance()?.uid ?? ++customTimePickerFallbackId
    const resolvedInputId = computed(
      () => props.inputId || `ctp-${instanceUid}`
    )
    const invalidFeedbackId = computed(
      () => `${resolvedInputId.value}-invalid-feedback`
    )
    const ariaLabelsForPicker = computed(() => {
      const label =
        props.accessibleLabel?.trim?.() || props.placeholder?.trim?.() || ""
      return {
        input: label || "Time"
      }
    })
    const resolvedAccessibleLabel = computed(() => {
      return ariaLabelsForPicker.value?.input || "Time"
    })
    const applyInputIconAccessibility = (rootEl, labelText, isDisabled) => {
      const iconTrigger = rootEl?.querySelector?.(INPUT_ICON_SELECTOR)
      if (!iconTrigger) return

      if (isDisabled) {
        iconTrigger.setAttribute("role", "presentation")
        iconTrigger.setAttribute("tabindex", "-1")
        iconTrigger.setAttribute("aria-disabled", "true")
        iconTrigger.removeAttribute("aria-label")
        return
      }

      iconTrigger.setAttribute("role", "button")
      iconTrigger.setAttribute("tabindex", "0")
      iconTrigger.removeAttribute("aria-disabled")
      iconTrigger.setAttribute(
        "aria-label",
        `Open ${labelText || "time picker"}`
      )

      if (iconTrigger.dataset?.keyboardReady === "true") return

      iconTrigger.addEventListener("keydown", (event) => {
        const isEnter = event?.key === "Enter"
        const isSpace = event?.key === " " || event?.key === "Spacebar"
        if (!isEnter && !isSpace) return
        event.preventDefault()
        iconTrigger.click()
      })
      iconTrigger.dataset.keyboardReady = "true"
    }

    const syncDisabledFocusableState = (rootEl, isDisabled) => {
      if (!rootEl) return

      const focusableElements = rootEl.querySelectorAll(FOCUSABLE_SELECTOR)
      focusableElements.forEach((element) => {
        if (!element) return
        const hasTabIndex = element.hasAttribute("tabindex")
        const currentTabIndex = element.getAttribute("tabindex") || ""

        if (isDisabled) {
          if (!element.hasAttribute(PRE_DISABLED_TABINDEX_ATTR)) {
            element.setAttribute(
              PRE_DISABLED_TABINDEX_ATTR,
              hasTabIndex ? currentTabIndex : "__none__"
            )
          }
          element.setAttribute("tabindex", "-1")
          return
        }

        const previousTabIndex = element.getAttribute(
          PRE_DISABLED_TABINDEX_ATTR
        )
        if (previousTabIndex === null) return

        if (previousTabIndex === "__none__") {
          element.removeAttribute("tabindex")
        } else {
          element.setAttribute("tabindex", previousTabIndex)
        }
        element.removeAttribute(PRE_DISABLED_TABINDEX_ATTR)
      })
    }
    //refs
    const datepicker = ref()
    const isTimeMenuOpen = ref(false)

    /** Without this, Tab leaves the menu and the picker closes before focus can reach increment buttons (@vuepic/vue-datepicker + text-input disabled overlay focus). */
    const datePickerConfig = { tabOutClosesMenu: false }

    /** Enables typing in the field; must match display `format` for parsing (see @vuepic/vue-datepicker text-input). */
    const timePickerTextInputOptions = computed(() => {
      if (props.disabled || props.readonly) {
        return false
      }
      const displayFormat = props.pickerTimeFormat || "hh:mm aa"
      return {
        enterSubmit: true,
        // Must be false: library Tab handler would run after our capture trap and block Tab from
        // moving focus from the input into the time columns (hours/minutes/AM-PM).
        tabSubmit: false,
        openMenu: "open",
        selectOnFocus: true,
        format: displayFormat
      }
    })

    /** Mirrors template `:is-24="!!showSeconds"` — digit entry for hours only in 24h mode. */
    const uses24HourClock = computed(() => !!props.showSeconds)

    let digitEntryBuffer = ""
    let digitEntrySegment = null
    let digitEntryTimer = null

    const resetDigitEntryBuffer = () => {
      digitEntryBuffer = ""
      digitEntrySegment = null
      if (digitEntryTimer) {
        clearTimeout(digitEntryTimer)
        digitEntryTimer = null
      }
    }

    const resolvedTimeFormat = () =>
      props.format || (props.showSeconds ? "HH:mm:ss" : "HH:mm")

    const emitParsedMoment = (m) => {
      const fmt = resolvedTimeFormat()
      const out = m.format(fmt)
      emit("update:modelValue", out)
      emit("change", out)
    }

    const flushPartialDigitBuffer = () => {
      if (!digitEntryBuffer || !digitEntrySegment) return
      const fmt = resolvedTimeFormat()
      const m = moment(props.modelValue, fmt, true)
      if (!m.isValid()) return
      const buf = digitEntryBuffer
      if (
        digitEntrySegment === "hours" &&
        uses24HourClock.value &&
        buf.length === 1
      ) {
        let h = parseInt(buf, 10)
        if (!Number.isNaN(h)) {
          h = Math.min(23, Math.max(0, h))
          m.hours(h)
          emitParsedMoment(m)
        }
      } else if (digitEntrySegment === "minutes" && buf.length === 1) {
        let v = parseInt(buf, 10)
        if (!Number.isNaN(v)) {
          v = Math.min(59, Math.max(0, v))
          m.minutes(v)
          emitParsedMoment(m)
        }
      } else if (digitEntrySegment === "seconds" && buf.length === 1) {
        let v = parseInt(buf, 10)
        if (!Number.isNaN(v)) {
          v = Math.min(59, Math.max(0, v))
          m.seconds(v)
          emitParsedMoment(m)
        }
      }
    }

    const scheduleDigitBufferReset = () => {
      if (digitEntryTimer) clearTimeout(digitEntryTimer)
      digitEntryTimer = setTimeout(() => {
        flushPartialDigitBuffer()
        resetDigitEntryBuffer()
      }, 900)
    }

    const applyDigitToTimeSegment = (segment, digitChar) => {
      if (segment === "hours" && !uses24HourClock.value) return

      if (digitEntrySegment !== segment) {
        digitEntryBuffer = ""
        digitEntrySegment = segment
      }
      digitEntryBuffer += digitChar
      scheduleDigitBufferReset()

      const fmt = resolvedTimeFormat()
      const m = moment(props.modelValue, fmt, true)
      if (!m.isValid()) return

      const d0 = parseInt(digitChar, 10)

      if (segment === "hours") {
        if (digitEntryBuffer.length === 1) {
          if (d0 > 2) {
            m.hours(Math.min(23, Math.max(0, d0)))
            emitParsedMoment(m)
            resetDigitEntryBuffer()
          }
        } else {
          let h = parseInt(digitEntryBuffer.slice(0, 2), 10)
          if (!Number.isNaN(h)) {
            h = Math.min(23, Math.max(0, h))
            m.hours(h)
            emitParsedMoment(m)
            resetDigitEntryBuffer()
          }
        }
        return
      }

      if (segment === "minutes" || segment === "seconds") {
        if (digitEntryBuffer.length === 1) {
          if (d0 > 5) {
            const v = Math.min(59, Math.max(0, d0))
            if (segment === "minutes") m.minutes(v)
            else m.seconds(v)
            emitParsedMoment(m)
            resetDigitEntryBuffer()
          }
        } else {
          let v = parseInt(digitEntryBuffer.slice(0, 2), 10)
          if (!Number.isNaN(v)) {
            v = Math.min(59, Math.max(0, v))
            if (segment === "minutes") m.minutes(v)
            else m.seconds(v)
            emitParsedMoment(m)
            resetDigitEntryBuffer()
          }
        }
      }
    }

    /**
     * Middle column is a button (Enter/Space opens overlay in the library). Add direct digit entry
     * for 24h hours + minutes + seconds when that control is focused.
     */
    const handleTimeDisplayKeyboard = (event, btn) => {
      const segment = segmentFromTimeDisplayBtn(btn)
      if (!segment || !/^[0-9]$/.test(event.key)) return false
      if (segment === "hours" && !uses24HourClock.value) return false

      event.preventDefault()
      applyDigitToTimeSegment(segment, event.key)
      return true
    }

    const getPickerRoot = () => datepicker.value?.$el ?? null

    const isVisibleFocusable = (el) => {
      if (!el || typeof el.getBoundingClientRect !== "function") return false
      const r = el.getBoundingClientRect()
      return r.width > 0 && r.height > 0
    }

    const getMenuWrap = () =>
      getPickerRoot()?.querySelector(MENU_WRAP_SELECTOR) ?? null

    const getMenuFocusables = () => {
      const menu = getMenuWrap()
      if (!menu) return []
      let list = Array.from(menu.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        isVisibleFocusable
      )
      if (!list.length) {
        list = Array.from(
          menu.querySelectorAll(
            "button.dp__inc_dec_button:not([disabled]), button.dp__time_display:not([disabled]), button.dp__pm_am_button:not([disabled])"
          )
        ).filter(isVisibleFocusable)
      }
      return list
    }

    const focusFirstMenuControl = () => {
      const menu = getMenuWrap()
      if (!menu) return
      const buttons = menu.querySelectorAll("button:not([disabled])")
      for (const btn of buttons) {
        if (isVisibleFocusable(btn)) {
          btn.focus({ preventScroll: true })
          return
        }
      }
      const list = getMenuFocusables()
      list[0]?.focus?.({ preventScroll: true })
    }

    const focusNextFocusableAfterPicker = () => {
      const container = getPickerRoot()?.closest(".custom-vue-time-picker")
      const modal = container?.closest?.(".modal-dialog, .modal-v3, .modal")
      const inputEl = getPickerRoot()?.querySelector(".dp__input")
      if (!container) {
        inputEl?.focus?.({ preventScroll: true })
        return
      }
      if (!modal) {
        inputEl?.focus?.({ preventScroll: true })
        return
      }
      const all = Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        isVisibleFocusable
      )
      let lastIdx = -1
      for (let i = 0; i < all.length; i++) {
        if (container.contains(all[i])) lastIdx = i
      }
      if (lastIdx >= 0 && lastIdx < all.length - 1) {
        all[lastIdx + 1]?.focus?.({ preventScroll: true })
      } else {
        inputEl?.focus?.({ preventScroll: true })
      }
    }

    const onMenuTabTrapKeydown = (event) => {
      if (event.key !== "Tab" || !isTimeMenuOpen.value) return
      const menu = getMenuWrap()
      if (!menu) return
      let items = getMenuFocusables()
      if (!items.length) {
        focusFirstMenuControl()
        items = getMenuFocusables()
      }
      if (!items.length) return

      const { activeElement } = document
      const inputEl = getPickerRoot()?.querySelector(".dp__input")
      const iconEl = getPickerRoot()?.querySelector(INPUT_ICON_SELECTOR)

      if (event.shiftKey) {
        if (activeElement === items[0]) {
          event.preventDefault()
          inputEl?.focus?.({ preventScroll: true })
          return
        }
        return
      }

      if (
        activeElement &&
        (activeElement === inputEl || activeElement === iconEl)
      ) {
        event.preventDefault()
        items[0]?.focus?.({ preventScroll: true })
        return
      }

      if (activeElement === items[items.length - 1]) {
        event.preventDefault()
        datepicker.value?.closeMenu?.()
        nextTick(() => {
          focusNextFocusableAfterPicker()
        })
      }
    }

    const onPickerMenuCapture = (event) => {
      if (!isTimeMenuOpen.value) return
      const target = event.target
      if (
        target?.matches?.("button.dp__time_display:not([disabled])") &&
        handleTimeDisplayKeyboard(event, target)
      ) {
        return
      }
      if (event.key === "Escape") {
        event.stopPropagation()
        return
      }
      if (event.key === "Tab") {
        onMenuTabTrapKeydown(event)
      }
    }

    const attachPickerMenuCapture = () => {
      document.addEventListener("keydown", onPickerMenuCapture, true)
    }

    const detachPickerMenuCapture = () => {
      document.removeEventListener("keydown", onPickerMenuCapture, true)
    }

    onUnmounted(() => {
      detachPickerMenuCapture()
      resetDigitEntryBuffer()
    })

    //watcher
    watch(
      () => props.show,
      (value) => {
        setTimeout(() => {
          if (value) {
            datepicker.value.openMenu()
          }
        }, 0)
      }
    )

    //methods
    const onValueChange = (value) => {
      emit("update:modelValue", value)
      emit("change", value)
    }
    const onBlur = () => {
      emit("blur")
    }
    const onPickerOpen = () => {
      if (!props.modelValue) {
        const fallback = moment().format(props.format ? props.format : "h:mm A")
        emit("update:modelValue", fallback)
        emit("change", fallback)
      }
      isTimeMenuOpen.value = true
      resetDigitEntryBuffer()
      attachPickerMenuCapture()
      nextTick(() => {
        const inputEl = getPickerRoot()?.querySelector(".dp__input")
        if (
          !props.disabled &&
          !props.readonly &&
          timePickerTextInputOptions.value &&
          inputEl &&
          typeof inputEl.focus === "function"
        ) {
          inputEl.focus({ preventScroll: true })
          return
        }
        focusFirstMenuControl()
        if (!getMenuFocusables().length) {
          requestAnimationFrame(() => {
            focusFirstMenuControl()
          })
        }
      })
    }

    const onPickerClosed = () => {
      isTimeMenuOpen.value = false
      resetDigitEntryBuffer()
      detachPickerMenuCapture()
    }

    const onClickOutside = () => {
      datepicker.value.closeMenu()
      emit("blur")
    }

    const invalidFeedbackMessage = computed(() => {
      return props.invalidFeedback && Array.isArray(props.invalidFeedback)
        ? props.invalidFeedback[0]
        : props.invalidFeedback
    })

    watchEffect(() => {
      const msg = invalidFeedbackMessage.value
      const fbId = invalidFeedbackId.value
      const accessibleInputLabel = resolvedAccessibleLabel.value
      const externalLabelId = props.labelId?.trim?.() || ""
      const isDisabled = !!props.disabled
      nextTick(() => {
        const root = datepicker.value?.$el
        const input = root?.querySelector("input")
        if (!input) return
        input.setAttribute("id", resolvedInputId.value)
        if (externalLabelId) {
          input.setAttribute("aria-labelledby", externalLabelId)
          input.removeAttribute("aria-label")
        } else {
          input.removeAttribute("aria-labelledby")
          input.setAttribute("aria-label", accessibleInputLabel)
        }
        if (isDisabled) {
          input.setAttribute("tabindex", "-1")
        } else {
          input.removeAttribute("tabindex")
        }
        applyInputIconAccessibility(root, accessibleInputLabel, isDisabled)
        syncDisabledFocusableState(root, isDisabled)
        if (msg && fbId) {
          input.setAttribute("aria-invalid", "true")
          input.setAttribute("aria-describedby", fbId)
        } else {
          input.removeAttribute("aria-invalid")
          input.removeAttribute("aria-describedby")
        }
      })
    })

    return {
      resolvedInputId,
      invalidFeedbackId,
      ariaLabelsForPicker,
      resolvedAccessibleLabel,
      datePickerConfig,
      timePickerTextInputOptions,
      onValueChange,
      onPickerOpen,
      onPickerClosed,
      onBlur,
      datepicker,
      onClickOutside,
      invalidFeedbackMessage
    }
  }
}
</script>
<style lang="scss">
@import "src/assets/scss/_variables";
@import "src/assets/v3scss/_variables";
.custom-vue-time-picker {
  .dp__instance_calendar {
    max-height: 200px;
  }
  .dp__time_display {
    font-size: 20px !important;
    padding: 20px 10px !important;
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: 2px solid $outline-color !important;
      outline-offset: $outline-offset;
      z-index: 1;
    }
  }
  .dp__pm_am_button {
    background-color: $accent-blue-dark !important;
    padding: 8px 12px !important;
    border-radius: 15px !important;
  }
  .dp__inc_dec_button {
    font-size: 20px;
    padding: 22px;
    border-radius: 0px;
    color: $gray-darken-50 !important;
    background-color: $white !important;
    &:hover {
      color: $accent-blue-dark;
    }
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: 2px solid $outline-color !important;
      outline-offset: $outline-offset;
      z-index: 1;
    }
  }
  .dp__overlay_container {
    height: 100% !important;
  }
  .dp__button.dp__overlay_action {
    display: none;
  }
  .form-control,
  .dp__input {
    border-color: $accent-blue-dark !important;
    padding: $input-padding-y $input-padding-x !important;
    min-height: 40px;
    height: auto !important;
    border-radius: $box-border-radius !important;
    border-width: $input-border-width;
    font-size: $formfieldsFontSize;
    font-weight: normal;
    line-height: 1.5;
    font-family: "Metropolis-Regular";
    color: $gray-darken-50 !important;
    background-color: $white !important;
    &::placeholder {
      color: $gray-darken-50 !important;
      opacity: 1;
    }
    &::-webkit-input-placeholder {
      color: $gray-darken-50 !important;
      opacity: 1;
    }
    &::-moz-placeholder {
      color: $gray-darken-50 !important;
      opacity: 1;
    }
    &:focus {
      outline: none;
      box-shadow: none !important;
    }
    &:focus-visible {
      outline: 2px solid $outline-color !important;
      outline-offset: $outline-offset;
    }
  }
  .dp__input_icon {
    color: $accent-blue-dark !important;
    left: auto;
    right: 15px;
    display: flex;
    align-items: center;
    &:focus-visible {
      outline: 2px solid $outline-color !important;
      outline-offset: $outline-offset;
      border-radius: 4px;
    }
    i {
      pointer-events: none;
    }
  }

  &.is-invalid {
    .form-control {
      border-color: #e55353 !important;
    }

    .invalid-feedback {
      display: block;
      z-index: 1;
    }
  }

  .timepicker {
    z-index: 999;

    .btn {
      box-shadow: none !important;

      &.btn-primary {
        padding: 12px;
      }
    }
  }

  .bootstrap-datetimepicker-widget a {
    padding: 0 !important;
  }
}
</style>
